import React, { useState, useMemo } from 'react';
import { GaugeChart, BarChart, LineChart, getMoodColor, getWeekdayLabels, WEEKDAY_ORDER } from './charts';
import { StatsFilters, type StatsFilters as StatsFiltersType, type CategoryOption, type StatsFiltersLabels } from './StatsFilters';
import { StatsEmptyState, type StatsEmptyStateLabels } from './StatsEmptyState';
import { NumericDependenciesChart, type NumericFieldData } from '../../components/NumericDependenciesChart';

export interface FieldInfo {
  name: string;
  averageMood: number;
  influence: number;
  occurrences: number;
}

export interface StatsData {
  empty: boolean;
  moodByDate: Record<string, number>;
  averageMoodPerWeekday: Record<number, number>;
  activitiesPercentagePerWeekday: Record<number, number>;
  totalAverageMood: number;
  medianMood: number;
  mostInfluencingFields: FieldInfo[];
  oldestEntryDate: string;
  numericDependencies: NumericFieldData[];
  unviewedReportsCount: number;
  hasFieldIds: boolean;
}

export interface StatsPageLabels {
  title: string;
  downloadCsv: string;
  averageMood: string;
  mostCommonMood: string;
  activitiesFrequencyByWeekday: string;
  moodByWeekday: string;
  moodByDay: string;
  mostInfluencingFields: string;
  seeAll: string;
  reports: string;
  newReportsCount: string;
  reportsDescription: string;
  viewAllReports: string;
  numericDependencies: string;
  numericDependenciesYAxis: string;
  numericDependenciesXAxis: string;
  noDataMessage: string;
  filters: StatsFiltersLabels;
  emptyState: StatsEmptyStateLabels;
}

export interface StatsPageProps {
  data: StatsData;
  filters: StatsFiltersType;
  categories: CategoryOption[];
  onFiltersChange: (filters: StatsFiltersType) => void;
  exportCsvUrl: string;
  reportsUrl: string;
  influencingFieldsUrl: string;
  diaryUrl: string;
  defaultPeriodStart: string;
  defaultPeriodEnd: string;
  labels: StatsPageLabels;
  isLoading?: boolean;
}

export function StatsPage({
  data,
  filters,
  categories,
  onFiltersChange,
  exportCsvUrl,
  reportsUrl,
  influencingFieldsUrl,
  diaryUrl,
  defaultPeriodStart,
  defaultPeriodEnd,
  labels,
  isLoading = false,
}: StatsPageProps) {
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const filtersApplied = filters.fieldIds.length > 0 ||
    filters.periodStart !== defaultPeriodStart ||
    filters.periodEnd !== defaultPeriodEnd;

  // Calculate average mood
  const averageMood = useMemo(() => {
    const values = Object.values(data.moodByDate);
    if (values.length === 0) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }, [data.moodByDate]);

  // Prepare weekday chart data
  const weekdayLabels = getWeekdayLabels();
  const moodByWeekdayData = useMemo(() => {
    return WEEKDAY_ORDER.map(wday => Math.round(data.averageMoodPerWeekday[wday] || 0));
  }, [data.averageMoodPerWeekday]);

  const frequencyByWeekdayData = useMemo(() => {
    return WEEKDAY_ORDER.map(wday => Math.round(data.activitiesPercentagePerWeekday[wday] || 0));
  }, [data.activitiesPercentagePerWeekday]);

  // Prepare mood by date data (sorted by date)
  const moodByDateData = useMemo(() => {
    return Object.entries(data.moodByDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({ label: date, value }));
  }, [data.moodByDate]);

  // Prepare influencing fields data
  const influencingFieldsLabels = useMemo(() => {
    return data.mostInfluencingFields.map(f => f.name);
  }, [data.mostInfluencingFields]);

  const influencingFieldsData = useMemo(() => {
    return data.mostInfluencingFields.map(f => f.influence);
  }, [data.mostInfluencingFields]);

  const influencingFieldsColors = useMemo(() => {
    return data.mostInfluencingFields.map(f =>
      f.influence >= 0 ? getMoodColor(100) : getMoodColor(-100)
    );
  }, [data.mostInfluencingFields]);

  const toggleFilters = () => setFiltersExpanded(!filtersExpanded);

  return (
    <div className="stats-page">
      {/* Header */}
      <h1 className="stats-page-title">
        {labels.title}
        <div className="float-end">
          <a
            href={exportCsvUrl}
            className="btn btn-sm btn-success me-2 stats-export-button"
            title={labels.downloadCsv}
          >
            <i className="bi bi-download"></i>
          </a>
          <button
            type="button"
            className={`btn btn-sm btn-primary stats-filter-button ${filtersApplied ? 'has-filters' : ''}`}
            onClick={toggleFilters}
          >
            <i className={`bi bi-funnel${filtersApplied ? '-fill' : ''}`}></i>
          </button>
        </div>
      </h1>

      {/* Filters */}
      <StatsFilters
        filters={filters}
        categories={categories}
        oldestEntryDate={data.oldestEntryDate}
        defaultPeriodStart={defaultPeriodStart}
        defaultPeriodEnd={defaultPeriodEnd}
        onChange={onFiltersChange}
        labels={labels.filters}
        isExpanded={filtersExpanded}
        onToggleExpand={toggleFilters}
      />

      {/* Reports Section */}
      <div className="mt-4 mb-4">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{labels.reports}</h4>
            {data.unviewedReportsCount > 0 && (
              <span className="badge bg-success">
                {labels.newReportsCount.replace('{count}', String(data.unviewedReportsCount))}
              </span>
            )}
          </div>
          <div className="card-body">
            <p className="text-muted">{labels.reportsDescription}</p>
            <a href={reportsUrl} className="btn btn-primary w-100">
              <i className="bi bi-file-text me-2"></i>
              {labels.viewAllReports}
            </a>
          </div>
        </div>
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Empty State or Charts */}
      {!isLoading && data.empty ? (
        <StatsEmptyState diaryUrl={diaryUrl} labels={labels.emptyState} />
      ) : !isLoading && (
        <>
          {/* Gauge Charts Row */}
          <div className="row">
            <div className="col-12 col-md-6 mt-4">
              <div className="card">
                <h4 className="card-header">{labels.averageMood}</h4>
                <div className="card-body">
                  <GaugeChart value={averageMood} />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 mt-4">
              <div className="card">
                <h4 className="card-header">{labels.mostCommonMood}</h4>
                <div className="card-body">
                  <GaugeChart value={data.medianMood} />
                </div>
              </div>
            </div>
          </div>

          {/* Activity Frequency (only shown when field filter is applied) */}
          {data.hasFieldIds && (
            <div className="row">
              <div className="col-12 col-md-6 mt-4">
                <div className="card">
                  <h4 className="card-header">{labels.activitiesFrequencyByWeekday}</h4>
                  <div className="card-body">
                    <BarChart
                      data={frequencyByWeekdayData}
                      labels={weekdayLabels}
                      height={250}
                      maxY={100}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mood by Weekday and Mood by Date */}
          <div className="row">
            <div className="col-12 col-md-6 mt-4">
              <div className="card">
                <h4 className="card-header">{labels.moodByWeekday}</h4>
                <div className="card-body">
                  <BarChart
                    data={moodByWeekdayData}
                    labels={weekdayLabels}
                    height={400}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 mt-4">
              <div className="card">
                <h4 className="card-header">{labels.moodByDay}</h4>
                <div className="card-body">
                  <LineChart
                    data={moodByDateData}
                    height={400}
                    minY={-100}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Most Influencing Fields */}
          {data.mostInfluencingFields.length > 1 && (
            <div className="row">
              <div className="col-12 mt-4">
                <div className="card">
                  <h4 className="card-header">
                    {labels.mostInfluencingFields}
                    <div className="float-end">
                      <a href={influencingFieldsUrl} className="btn btn-sm btn-primary">
                        {labels.seeAll}
                      </a>
                    </div>
                  </h4>
                  <div className="card-body">
                    <BarChart
                      data={influencingFieldsData}
                      labels={influencingFieldsLabels}
                      colors={influencingFieldsColors}
                      horizontal
                      height={300}
                      showLegend={false}
                      useMoodColors={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Numeric Dependencies */}
          {data.numericDependencies.length > 0 && (
            <div className="row">
              <div className="col-12 mt-4">
                <div className="card">
                  <h4 className="card-header">{labels.numericDependencies}</h4>
                  <div className="card-body">
                    <NumericDependenciesChart
                      fields={data.numericDependencies}
                      labels={{
                        yAxisLabel: labels.numericDependenciesYAxis,
                        xAxisLabel: labels.numericDependenciesXAxis,
                        noDataMessage: labels.noDataMessage,
                      }}
                      height={400}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StatsPage;
